
import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { logger } from "@/lib/logger";
import { SlackIntegration } from "../../types";

// Cache duration for Slack integration data
const SLACK_CACHE_DURATION = 300000; // 5 minutes

/**
 * Hook for fetching and caching Slack integration data
 */
export const useSlackData = (
  aiStaffId: string,
  loadIntegration: () => Promise<SlackIntegration | null>,
  stateSetters: {
    setIsConnected: (connected: boolean) => void;
    setWebhookUrl: (url: string) => void;
    setSlackToken: (token: string) => void;
    setTriggerTag: (tag: string) => void;
    setIsFileProcessingEnabled: (enabled: boolean) => void;
    setIsAutoResponsesEnabled: (enabled: boolean) => void;
    setIsRoleDetectionEnabled: (enabled: boolean) => void;
    setConnectionError: (error: string | null) => void;
    setIsChecking: (checking: boolean) => void;
    setIsInitialized: (initialized: boolean) => void;
  }
) => {
  const initialLoadComplete = useRef(false);
  const {
    setIsConnected,
    setWebhookUrl,
    setSlackToken,
    setTriggerTag,
    setIsFileProcessingEnabled,
    setIsAutoResponsesEnabled,
    setIsRoleDetectionEnabled,
    setConnectionError,
    setIsChecking,
    setIsInitialized
  } = stateSetters;

  // Use React Query to cache and efficiently load integration data
  const { data: integrationData } = useQuery({
    queryKey: ['slackIntegration', aiStaffId, 'data'],
    queryFn: async () => {
      if (!aiStaffId || initialLoadComplete.current) {
        return null;
      }
      
      try {
        setIsChecking(true);
        const data = await loadIntegration();
        
        if (data) {
          setIsConnected(true);
          setWebhookUrl(data.webhook_url || "");
          setSlackToken(data.slack_token || "");
          setTriggerTag(data.trigger_tag || "@marketing");
          setIsFileProcessingEnabled(!!data.is_file_processing_enabled);
          setIsAutoResponsesEnabled(!!data.is_auto_responses_enabled);
          setIsRoleDetectionEnabled(false);
          setConnectionError(null);
        } else {
          setIsConnected(false);
        }
        
        initialLoadComplete.current = true;
        return data;
      } catch (error) {
        logger.error({
          message: "Error loading Slack integration",
          context: { aiStaffId },
          error: error instanceof Error ? error : new Error(String(error))
        });
        setConnectionError("Failed to load integration data");
        return null;
      } finally {
        setIsChecking(false);
        setIsInitialized(true);
      }
    },
    staleTime: SLACK_CACHE_DURATION,
    enabled: !initialLoadComplete.current && !!aiStaffId,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  return { integrationData };
};
